import {
  Box,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useCallback, useEffect, useRef, useState } from "react";
import _ from "lodash";
import Circle from "./Circle.tsx";
import FormattedMessageJamezz from "../../../global/components/FormattedMessageJamezz.tsx";
import { useCustomTexts } from "../../../global/utils/useCustomTexts.tsx";

export default function TicketPincodeDialog({
  numberOfDigits = 4,
  onPincodeEntered,
  onClose,
}: {
  numberOfDigits?: number;
  onPincodeEntered?: (pincode: string) => Promise<boolean>;
  onClose?: () => void; // Keep onClose prop type
}) {
  const theme = useTheme();
  const [pincode, setPincode] = useState("");
  const [pincodeIsWrong, setPincodeIsWrong] = useState("");

  const inputRefs = useRef<(HTMLInputElement | null)[]>(_.times(numberOfDigits, _.constant(null)));

  const onChange = useCallback(
    (key: string) => {
      if (key == "Backspace") {
        if (pincode.length > 0) {
          setPincode(pincode.substring(0, pincode.length - 1));
          setPincodeIsWrong("");
        }
      } else {
        if (key.length == 1) {
          setPincode((pincode + key).substring(0, numberOfDigits));
          setPincodeIsWrong("");
        }
      }
    },
    [numberOfDigits, pincode]
  );

  useEffect(() => {
    inputRefs.current[pincode.length]?.focus();
  }, [pincode]);

  useEffect(() => {
    if (pincode.length === numberOfDigits) {
      onPincodeEntered?.(pincode).then((result) => {
        if (!result) {
          setPincode("");
          setPincodeIsWrong(new Date().toISOString());
        }
      });
    }
  }, [pincode, onPincodeEntered, numberOfDigits]);

  const ct = useCustomTexts(["use_random_pincodes_enter_pincode_explanation"]);

  const body = (
    <>
      {/* Use DialogTitle's ability to render children for layout */}
      <DialogTitle sx={{ m: 0, p: 2 }} id="pincode-dialog-title">
        {/* Flex container for Title and Button */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {/* Title Text */}
          <FormattedMessageJamezz id={"Please enter pincode..."} />
          {/* Close Button */}
          {onClose ? (
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                color: (theme) => theme.palette.grey[500],
                // Add some negative margin if needed to align perfectly with edges
                // marginRight: -1,
                // marginTop: -1,
              }}
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{ pb: 4 }}
        onKeyDown={(e) => {
          onChange(e.key);
        }}
      >
        <Typography sx={{ mb: 2 }}>{ct["use_random_pincodes_enter_pincode_explanation"]}</Typography>
        <Collapse in={Boolean(pincodeIsWrong)}>
          <Typography sx={{ mb: 2, color: theme.palette.error.main }}>
            <FormattedMessageJamezz id={"Pincode is invalid. Try again..."} />
          </Typography>
        </Collapse>
        <Stack direction={"row"} gap={1} justifyContent={"center"}>
          {_.times(numberOfDigits).map((index) => (
            <Circle
              key={index}
              index={index}
              isFilled={pincode.charAt(index) != ""}
              inputRefs={inputRefs}
              onFocus={() => {
                inputRefs.current[pincode.length]?.focus();
              }}
            />
          ))}
        </Stack>
      </DialogContent>
    </>
  );
  return onClose ? (
    body
  ) : (
    <Dialog
      open={true}
      onKeyDown={(e) => {
        onChange(e.key);
      }}
    >
      {body}
    </Dialog>
  );
}
